/**
 * Breadcrumb Navigation Component
 * Provides contextual navigation showing the user's current location
 */

import { useLocation, Link } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon } from '@heroicons/react/24/outline';

interface BreadcrumbItem {
  name: string;
  href: string;
  current?: boolean;
}

interface BreadcrumbProps {
  className?: string;
}

// Define route mappings for better breadcrumb names
const routeNames: Record<string, string> = {
  dashboard: 'Dashboard',
  organizations: 'Organizaciones',
  calculations: 'Cálculos',
  reports: 'Reportes',
  analysis: 'Análisis Histórico',
  configuration: 'Configuración',
  users: 'Usuarios',
  profile: 'Mi Perfil',
  'new-calculation': 'Nuevo Cálculo',
  'new-organization': 'Nueva Organización',
  'user-management': 'Gestión de Usuarios',
  edit: 'Editar',
  details: 'Detalles',
};

export const Breadcrumb = ({ className = '' }: BreadcrumbProps) => {
  const location = useLocation();

  // Generate breadcrumb items from current path
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean);

    if (pathSegments.length === 0 || pathSegments[0] === 'dashboard') {
      return [];
    }

    const breadcrumbs: BreadcrumbItem[] = [
      {
        name: 'Dashboard',
        href: '/dashboard',
      },
    ];

    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === pathSegments.length - 1;

      // Skip dynamic ID segments (those that are UUIDs or contain only numbers/hyphens)
      if (/^[a-f0-9-]+$/i.test(segment) || /^\d+$/.test(segment)) {
        return;
      }

      breadcrumbs.push({
        name:
          routeNames[segment] ||
          segment.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()),
        href: currentPath,
        current: isLast,
      });
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  // Don't render if we're on dashboard or have no breadcrumbs
  if (breadcrumbs.length <= 1) {
    return null;
  }

  return (
    <nav className={`flex ${className}`} aria-label="Breadcrumb">
      <ol className="flex items-center space-x-2">
        <li>
          <div>
            <Link
              to="/dashboard"
              className="text-gray-400 hover:text-gray-500 transition-colors"
              aria-label="Dashboard"
            >
              <HomeIcon className="h-5 w-5" />
            </Link>
          </div>
        </li>

        {breadcrumbs.slice(1).map(item => (
          <li key={item.href}>
            <div className="flex items-center">
              <ChevronRightIcon className="h-5 w-5 text-gray-300 flex-shrink-0" />
              {item.current ? (
                <span
                  className="ml-2 text-sm font-medium text-gray-500 truncate"
                  aria-current="page"
                >
                  {item.name}
                </span>
              ) : (
                <Link
                  to={item.href}
                  className="ml-2 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors truncate"
                >
                  {item.name}
                </Link>
              )}
            </div>
          </li>
        ))}
      </ol>
    </nav>
  );
};
